/* eslint-disable no-underscore-dangle */
import { merge } from 'lodash';
import { APILayerConsts } from 'vue-infra';
import { RESTART_SUBSCRIPTION_STATUS_CODES, RETRY_INTERVAL } from '@config/Consts';
import { urlResolver } from '@core/common/UrlResolver';
import BaseClass from './BaseClass';

// We want to retry once the HTTP status code matches specific codes
// from the RESTART_SUBSCRIPTION_STATUS_CODES array.
// If there are network issues, polling continues, therefore we should ignore 'ECONNABORTED'.
// If we don't even get error.response, and consequently we can't check the HTTP status code,
// we definitely want to retry sending the request
const _shouldPerformRetry = error => error.code !== 'ECONNABORTED'
  && (!error.response || (RESTART_SUBSCRIPTION_STATUS_CODES.indexOf(error.response.status) > -1));

const EVENTS = {
  onPollingSuccess: 'onPollingSuccess',
  onPollingError: 'onPollingError',
  onRevisionedPollingSuccess: 'onRevisionedPollingSuccess',
  onRevisionedPollingError: 'onRevisionedPollingError',
};

const RESPONSE_STATUSES = {
  SUCCESS: 200,
};

const getClientRetryConfig = {
  retryCondition: _shouldPerformRetry,
  shouldResetTimeout: true,
  retryDelay: () => RETRY_INTERVAL,
};

class BaseClient extends BaseClass {
  constructor(options) {
    super({ name: options.name, type: 'Client' });
    this.infraBaseClient = options.infraBaseClient;
    this.resource = options.resource;
  }

  subscribe(options) {
    this.listenToHttpClientEvents();
    return this.infraBaseClient.subscribe(merge(options, this.infraBaseClient.options));
  }

  unsubscribe(source) {
    this.infraBaseClient.unsubscribe(source);
    this.stopListenToHttpClientEvents();
  }

  listenToHttpClientEvents() {
    this.stopListenToHttpClientEvents();
    this.infraBaseClient.on(
      APILayerConsts.EVENTS.onSubscriptionUpdateNotification,
      this.emit.bind(this, EVENTS.onPollingSuccess),
      'baseClientPollingSuccess');
    this.infraBaseClient.on(
      APILayerConsts.EVENTS.onRevisionedSubscriptionUpdateNotification,
      this.emit.bind(this, EVENTS.onRevisionedPollingSuccess),
      'baseClientRevisionPollingSuccess');
    this.infraBaseClient.on(
      APILayerConsts.EVENTS.onSubscriptionError,
      this.emit.bind(this, EVENTS.onPollingError),
      'baseClientPollingError');
    this.infraBaseClient.on(
      APILayerConsts.EVENTS.onRevisionedSubscriptionError,
      this.emit.bind(this, EVENTS.onRevisionedPollingError),
      'baseClientRevisionPollingError');
  }

  stopListenToHttpClientEvents() {
    this.infraBaseClient.clearAllListeners(APILayerConsts.EVENTS.onSubscriptionUpdateNotification);
    this.infraBaseClient.clearAllListeners(
      APILayerConsts.EVENTS.onRevisionedSubscriptionUpdateNotification);
    this.infraBaseClient.clearAllListeners(APILayerConsts.EVENTS.onSubscriptionError);
    this.infraBaseClient.clearAllListeners(
      APILayerConsts.EVENTS.onRevisionedSubscriptionError);
  }

  async sendRequest(settings) {
    const method = settings.method;
    return this.infraBaseClient[method](
      settings.url, settings.body || settings.config, settings.config);
  }

  useRequestInterceptor(interceptor, errorHandler) {
    this.infraBaseClient.useRequestInterceptor(interceptor, errorHandler);
  }
}

BaseClient.HTTP_METHODS = APILayerConsts.CRUDMethods;
BaseClient.EVENTS = EVENTS;
BaseClient.RESPONSE_STATUSES = RESPONSE_STATUSES;
BaseClient.getClientRetryConfig = getClientRetryConfig;
BaseClient.urlResolver = urlResolver;

export default BaseClient;
